import React from 'react';
import uuid from 'react-native-uuid';

import {
  PlaceAutocompleteResult,
  PlaceAutocompleteSelection,
  getPlaceAutocomplete,
  getPlaceAutocompleteResults,
  getPlaceFromAutocompleteResult,
} from '@db/Places';
import {
  AppQueryDomain,
  AppQueryKeySegment,
  createQueryKey,
  nullableQueryKeyPart,
  useAppMutation,
  useAppQuery,
} from '@helpers/query';

type PlaceAutocompleteTypes = string | readonly string[] | undefined;

export type UsePlaceAutocompleteQueryOptions = {
  /** Set false when autocomplete should be held even if a query exists. */
  enabled?: boolean;
  query: string;
  sessionToken: string;
  types?: PlaceAutocompleteTypes;
};

export type UsePlaceAutocompleteSelectionVariables = {
  result: PlaceAutocompleteResult;
  sessionToken: string;
};

export function usePlaceAutocompleteSession() {
  const [sessionToken, setSessionToken] = React.useState(createPlaceAutocompleteSessionToken);
  const resetSessionToken = React.useCallback(() => {
    setSessionToken(createPlaceAutocompleteSessionToken());
  }, []);

  return { resetSessionToken, sessionToken };
}

export function usePlaceAutocompleteQuery({
  enabled = true,
  query,
  sessionToken,
  types,
}: UsePlaceAutocompleteQueryOptions) {
  const isEnabled = enabled && query.length > 0;
  const autocompleteQuery = useAppQuery({
    queryKey: createQueryKey(
      AppQueryDomain.PLACES,
      AppQueryKeySegment.AUTOCOMPLETE,
      query,
      createPlaceAutocompleteTypesKeyPart(types),
      nullableQueryKeyPart(sessionToken)
    ),
    queryFn: async () => getAutocompleteResults(query, types, sessionToken),
    enabled: isEnabled,
    placeholderData: [],
    retry: false,
  });

  return {
    error: autocompleteQuery.isError,
    loading: autocompleteQuery.isFetching,
    results: isEnabled ? (autocompleteQuery.data ?? []) : [],
  };
}

export function usePlaceAutocompleteSelectionMutation() {
  const isResolvingRef = React.useRef(false);
  const { isError, isPending, mutateAsyncOrThrow } = useAppMutation<
    PlaceAutocompleteSelection,
    Error,
    UsePlaceAutocompleteSelectionVariables
  >({
    mutationKey: createQueryKey(AppQueryDomain.PLACES, AppQueryKeySegment.SELECTION_MUTATION),
    mutationFn: ({ result, sessionToken }) => getPlaceFromAutocompleteResult(result, sessionToken),
  });

  const resolveResult = React.useCallback(
    async (result: PlaceAutocompleteResult, sessionToken: string) => {
      if (isResolvingRef.current) return undefined;

      isResolvingRef.current = true;
      try {
        return mutateAsyncOrThrow({ result, sessionToken });
      } finally {
        isResolvingRef.current = false;
      }
    },
    [mutateAsyncOrThrow]
  );

  return {
    error: isError,
    loading: isPending,
    resolveResult,
  };
}

function createPlaceAutocompleteSessionToken() {
  return uuid.v4().toString();
}

function createPlaceAutocompleteTypesKeyPart(types: PlaceAutocompleteTypes) {
  return typeof types === 'string' ? types : (types ?? null);
}

async function getAutocompleteResults(
  query: string,
  types: PlaceAutocompleteTypes,
  sessionToken: string
): Promise<PlaceAutocompleteResult[]> {
  const requestTypes = getPlaceAutocompleteRequestTypes(types);
  const response = await getPlaceAutocomplete(query, requestTypes, sessionToken);
  return getPlaceAutocompleteResults(response);
}

function getPlaceAutocompleteRequestTypes(
  types: PlaceAutocompleteTypes
): string | string[] | undefined {
  if (types === undefined) return undefined;
  if (typeof types === 'string') return types;

  return [...types];
}
